/**
 * Key Takeaway / TL;DR summary block for GEO optimization
 *
 * Provides a visually distinct, easily extractable summary block that
 * generative AI engines (ChatGPT, Perplexity, Claude) can cite directly.
 * Wrapped in semantic HTML (<aside>) with data attributes for speakable schema.
 */

import { Lightbulb } from 'lucide-react';

interface KeyTakeawayProps {
  /** The main takeaway text */
  children: React.ReactNode;
  /** Optional title (defaults to "Key Takeaway") */
  title?: string;
}

/**
 * KeyTakeaway component for GEO-optimized content
 *
 * Renders a visually distinct summary block that AI models can easily
 * extract and cite. Uses semantic HTML and data attributes for
 * speakable schema integration.
 *
 * @example
 * ```tsx
 * <KeyTakeaway>
 *   Email Ferret catches AI-generated cold outreach that Gmail's spam filter misses
 *   by analyzing 15+ behavioral signals including domain trust, sales intent, and
 *   automation tool fingerprints. It costs $5/month and never stores your email content.
 * </KeyTakeaway>
 * ```
 */
export function KeyTakeaway({ children, title = 'Key Takeaway' }: KeyTakeawayProps) {
  return (
    <aside
      data-speakable="summary"
      className="bg-gradient-to-r from-blue-500/10 via-purple-500/5 to-blue-500/10 border border-blue-500/20 rounded-xl p-6 my-8"
      role="note"
      aria-label={title}
    >
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="h-5 w-5 text-blue-400 flex-shrink-0" />
        <span className="text-sm font-semibold text-blue-400 uppercase tracking-wider">
          {title}
        </span>
      </div>
      <div className="text-foreground/90 leading-relaxed">{children}</div>
    </aside>
  );
}
